import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../firebase/useAuth';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useModal } from '../../context/ModalContext';
import { useToast } from '../../context/ToastContext';
import { fetchCramWordsItems } from '../../firebase/cramSession';
import { fetchRecentUnlocksWordsItems } from '../../firebase/recentUnlocks';
import { skipWord } from '../../firebase/words';
import { getSound } from '../../firebase/sounds';
import levelUpSoundFile from '../../assets/sounds/correct.mp3';

// Components
import SessionLayout from '../../components/session/SessionLayout';
import InputSection from '../../components/session/InputSection';
import InteractiveCard from '../../components/session/InteractiveCard';
import Spinner from '../../components/common/Spinner';
import DetailsModal from '../../components/modals/DetailsModal/DetailsModal';
import ReportIssueModal from '../../components/modals/ReportIssueModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SimpleAnswerSection from '../../components/session/SimpleAnswerSection';
import { AudioPlayerProvider } from '../../context/AudioPlayerContext';
import { checkWordAnswer } from '../../utils/answerCheck';

const CramWordsSessionPage = ({ mode = 'cram' }) => {
  const fetchFn = mode === 'cram' ? fetchCramWordsItems : fetchRecentUnlocksWordsItems;
  const sessionType = mode === 'cram' ? 'cram_words' : 'recent_unlocks_words';
  const emptyMessage = mode === 'cram' ? 'No words in your recent mistakes.' : 'No recently unlocked words to practice.';
  const { userId, userData, userLoading, emailVerified, subscriptionStatus } = useAuth();
  const { learnedWordIds, learnedSentenceIds, wordIdsLoading, sentenceIdsLoading } = useMetadata();
  const { showModal, hideModal, modalStack } = useModal();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Session state
  const [cramWords, setCramWords] = useState([]);
  const [correctWords, setCorrectWords] = useState([]);
  const [wrongWords, setWrongWords] = useState([]);
  const [markedWrongIds, setMarkedWrongIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [checkSubmitted, setCheckSubmitted] = useState(false);
  const [inputResults, setInputResults] = useState([]);
  const [lastProcessedIndex, setLastProcessedIndex] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFetchKey, setAudioFetchKey] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [userChoice, setUserChoice] = useState(null);
  const [currentWordId, setCurrentWordId] = useState(null);
  const [sessionSettings, setSessionSettings] = useState(() => {
    return (
      JSON.parse(localStorage.getItem('sessionSettings')) || {
        playCorrectSound: true,
        playWrongSound: true,
        showTranslation: true,
        inputMode: 'mixed',
        simpleAnswerMode: false,
        showFlippingAnimation: true,
        reducedAnimations: false,
        showKaraokeMode: true,
        autoPlayAfterAnswer: false,
        stayOnQuestionUntilCorrect: false,
      }
    );
  });
  const [pendingRetry, setPendingRetry] = useState(false);

  const inputRef = useRef(null);
  const initialFetchDone = useRef(false);
  const levelUpSound = useMemo(() => new Audio(levelUpSoundFile), []);
  const currentWord = cramWords[currentWordIndex];
  const isFullyLoading = loading || wordIdsLoading;

  // Auth guard
  useEffect(() => {
    if (userLoading) return;
    if (userId && !emailVerified) {
      navigate('/dashboard', { state: { errorMessage: 'Please verify your email to start a session.' } });
    }
  }, [userLoading, userId, emailVerified, navigate]);

  // Error message from navigation state
  useEffect(() => {
    if (location.state?.errorMessage) {
      showToast(location.state.errorMessage, 'error');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showToast, navigate, location.pathname]);

  // Determine voice when word changes
  useEffect(() => {
    setAudioUrl(null);
    if (currentWord && currentWord.sound_male && currentWord.sound_female) {
      const initialVoice =
        currentWord.preferred_voice === 'random'
          ? Math.random() < 0.5 ? 'male' : 'female'
          : currentWord.preferred_voice || 'male';
      setSelectedVoice(initialVoice);
    } else {
      setSelectedVoice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWord?.id]);

  useEffect(() => {
    if (currentWord?.id !== currentWordId) setCurrentWordId(currentWord?.id);
  }, [currentWord?.id, currentWordId]);

  // Fetch audio
  useEffect(() => {
    let isMounted = true;
    if (currentWord && currentWord.id === currentWordId) {
      if (currentWord.sound_male && currentWord.sound_female) {
        if (selectedVoice) {
          const soundFile = selectedVoice === 'male' ? currentWord.sound_male : currentWord.sound_female;
          getSound(soundFile, 'words').then((url) => {
            if (isMounted && currentWord.id === currentWordId) setAudioUrl(url);
          }).catch(console.error);
        }
      } else if (currentWord.sound) {
        getSound(currentWord.sound, 'words').then((url) => {
          if (isMounted && currentWord.id === currentWordId) setAudioUrl(url);
        }).catch(console.error);
      } else {
        setAudioUrl(null);
      }
    }
    return () => { isMounted = false; };
  }, [currentWord, selectedVoice, currentWordId, audioFetchKey]);

  // Focus input
  useEffect(() => {
    if (!isFullyLoading && currentWord && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFullyLoading, currentWord, checkSubmitted]);

  // Fetch cram words
  useEffect(() => {
    const fetchItems = async () => {
      if (!userId || !userData || initialFetchDone.current) return;
      // Claim the fetch before awaiting. `userData` is a live snapshot object,
      // so any user-doc write re-runs this effect and could otherwise slip past
      // the guard and start a second full session load.
      initialFetchDone.current = true;
      setLoading(true);
      try {
        const items = await fetchFn(userId, learnedSentenceIds);
        if (items.length === 0) {
          navigate('/dashboard', { state: { errorMessage: emptyMessage } });
          return;
        }
        setCramWords(items);
        setTotalCount(items.length);
      } catch (error) {
        initialFetchDone.current = false; // release the claim so a later snapshot can retry
        console.error('Error fetching cram words:', error);
        showToast('Failed to load cram session.', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading && userId && userData && !sentenceIdsLoading) {
      fetchItems();
    }
  }, [userId, userData, learnedSentenceIds, sentenceIdsLoading, navigate, userLoading, showToast, fetchFn, emptyMessage]);

  // Prevent accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.returnValue = 'Items you did not clear will remain in your cram list.';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => {
      if (e.touches?.[0]?.touchType === "stylus") return;
      let el = e.target;
      while (el && el !== document.body) {
        const { overflowY, overflowX } = window.getComputedStyle(el);
        if (
          ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) ||
          ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth)
        ) return;
        el = el.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', preventTouchMove);
  }, []);

  const handlePlayEnd = useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleWordClick = useCallback(
    (word) => showModal(<DetailsModal item={{ ...word }} handleClose={hideModal} />),
    [showModal, hideModal]
  );

  const handleSeeAlsoClick = useCallback(() => {
    showModal(<DetailsModal item={currentWord} handleClose={hideModal} initialTab="seeAlso" />);
  }, [showModal, hideModal, currentWord]);

  const handleExamplesClick = useCallback(() => {
    showModal(<DetailsModal item={currentWord} handleClose={hideModal} initialTab="examples" />);
  }, [showModal, hideModal, currentWord]);

  const handleReportClick = useCallback(
    (currentUserInput) => showModal(<ReportIssueModal item={currentWord} onClose={hideModal} userInput={currentUserInput} />),
    [showModal, hideModal, currentWord]
  );

  const handleCorrectReview = useCallback(() => {
    if (!currentWord) return;
    setUserChoice(true);
    setLastProcessedIndex(currentWordIndex);
    setCorrectCount((prev) => prev + 1);
    setSendResult('correct');
    if (sessionSettings?.playCorrectSound) levelUpSound.play();
  }, [currentWord, currentWordIndex, sessionSettings, levelUpSound]);

  const handleWrongReview = useCallback(() => {
    if (currentWord) setMarkedWrongIds((prev) => new Set(prev).add(currentWord.id));
    setUserChoice(false);
    setLastProcessedIndex(currentWordIndex);
    setSendResult('wrong');
    setPendingRetry(true);
  }, [currentWord, currentWordIndex]);

  const handleCheck = useCallback(() => {
    if (!currentWord) return;
    const { input, isCorrect: isInputCorrect, expected } = checkWordAnswer(currentWord, userInput, sessionSettings?.inputMode || 'mixed');

    setInputResults([{ correct: isInputCorrect, text: input, ...(isInputCorrect ? {} : { expected }) }]);
    setCheckSubmitted(true);
    setIsCorrect(isInputCorrect);

    if (isInputCorrect) {
      handleCorrectReview();
    } else {
      handleWrongReview();
    }
  }, [currentWord, userInput, sessionSettings, handleCorrectReview, handleWrongReview]);

  const navigateToCramEnd = useCallback(
    (finalCorrect, finalWrong) => {
      const sanitize = (w) => {
        const sanitized = { ...w, learned: true };
        delete sanitized.word_id;
        delete sanitized.word_ref;
        return sanitized;
      };
      navigate('/session/end', {
        state: {
          correctWords: finalCorrect.map(sanitize),
          wrongWords: finalWrong.map(sanitize),
          type: sessionType,
        },
      });
    },
    [navigate, sessionType]
  );

  const handleNextWord = useCallback(() => {
    const shouldStay = pendingRetry && sessionSettings?.stayOnQuestionUntilCorrect;
    setPendingRetry(false);
    if (shouldStay) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
      setUserChoice(null);
      setLastProcessedIndex(null);
      setSendResult(null);
      setAudioUrl(null);
      setAudioFetchKey((prev) => prev + 1);
      // Words are simply right or wrong — start the retry on a clean sheet
      // rather than preserving the wrong answer (unlike sentence sessions).
      setUserInput('');
      inputRef.current?.focus();
      return;
    }
    setUserChoice(null);
    setAudioUrl(null);
    setAudioFetchKey((prev) => prev + 1);

    if (lastProcessedIndex !== null) {
      let newCorrect = correctWords;
      let newWrong = wrongWords;

      if (sendResult === 'correct' && currentWord) {
        // Split based on whether item was ever wrong this session
        if (markedWrongIds.has(currentWord.id)) {
          newWrong = [...wrongWords, currentWord];
          setWrongWords(newWrong);
        } else {
          newCorrect = [...correctWords, currentWord];
          setCorrectWords(newCorrect);
        }

        setCramWords((prev) => {
          const updated = prev.filter((_, i) => i !== lastProcessedIndex);
          if (updated.length === 0) {
            navigateToCramEnd(newCorrect, newWrong);
            return [];
          }
          setCurrentWordIndex(Math.floor(Math.random() * updated.length));
          return updated;
        });
      } else {
        // Wrong — keep item in queue, pick a different one if possible
        setCramWords((prev) => {
          if (prev.length > 1) {
            let nextIndex;
            do {
              nextIndex = Math.floor(Math.random() * prev.length);
            } while (nextIndex === lastProcessedIndex);
            setCurrentWordIndex(nextIndex);
          }
          return prev;
        });
      }

      setLastProcessedIndex(null);
      setSendResult(null);
    } else {
      // Not yet answered — pick random different item
      if (cramWords.length > 1) {
        let nextIndex;
        do {
          nextIndex = Math.floor(Math.random() * cramWords.length);
        } while (nextIndex === currentWordIndex);
        setCurrentWordIndex(nextIndex);
      }
    }

    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIsCorrect(null);
    inputRef.current?.focus();
  }, [
    lastProcessedIndex,
    sendResult,
    currentWord,
    correctWords,
    wrongWords,
    markedWrongIds,
    cramWords,
    currentWordIndex,
    navigateToCramEnd,
    pendingRetry,
    sessionSettings?.stayOnQuestionUntilCorrect,
  ]);

  const handleSkipConfirm = useCallback(() => {
    if (!currentWord) return;
    skipWord(userId, currentWord.id, subscriptionStatus.isSubscribed)
      .then((result) => { if (result?.status === 'already_skipped') showToast('This word is already skipped.', 'info'); })
      .catch(console.error);
    if (sessionSettings?.playCorrectSound) levelUpSound.play();
    setAudioUrl(null);

    const newCorrect = [...correctWords, { ...currentWord, srs_stage: 7 }];
    setCorrectWords(newCorrect);
    setCramWords((prev) => {
      const updated = prev.filter((_, i) => i !== currentWordIndex);
      if (updated.length === 0) {
        navigateToCramEnd(newCorrect, wrongWords);
        return [];
      }
      setCurrentWordIndex(Math.floor(Math.random() * updated.length));
      return updated;
    });
    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIsCorrect(null);
    setUserChoice(null);
    inputRef.current?.focus();
  }, [currentWord, userId, subscriptionStatus.isSubscribed, sessionSettings, levelUpSound, correctWords, wrongWords, currentWordIndex, navigateToCramEnd, showToast]);

  const handleSkipClick = useCallback(() => {
    showModal(
      <ConfirmationModal
        type="skip"
        onClose={hideModal}
        onConfirm={() => { handleSkipConfirm(); hideModal(); }}
      />
    );
  }, [showModal, hideModal, handleSkipConfirm]);

  const handleSettingsUpdate = (newSettings) => {
    if (newSettings.simpleAnswerMode !== sessionSettings.simpleAnswerMode && userChoice === null) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
    }
    setSessionSettings(newSettings);
  };

  const handleSimpleModeChoice = (isCorrectChoice) => {
    if (userChoice !== null) return;
    setUserChoice(isCorrectChoice);
    setIsCorrect(isCorrectChoice);
    setInputResults([{ correct: isCorrectChoice, text: currentWord.word }]);
    if (isCorrectChoice) {
      handleCorrectReview();
    } else {
      handleWrongReview();
    }
    setCheckSubmitted(true);
  };

  const isAnyModalOpen = modalStack.length > 0;


  const [isPenMode, setIsPenMode] = useState(false);
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (e.pointerType === 'pen') {
        setIsPenMode(true);
      }
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  if ((isFullyLoading && cramWords.length === 0) || !currentWord)
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <Spinner />
      </div>
    );

  return (
    <div className="h-dvh overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 z-40">
          <SessionLayout
            updateSettings={handleSettingsUpdate}
            correctCount={correctCount}
            totalCount={totalCount}
          />
        </div>

        {/* Card area */}
        <div className={`${!sessionSettings.simpleAnswerMode && isPenMode ? 'flex-shrink flex-grow-0' : 'flex-1'} min-h-0 flex flex-col w-full max-w-4xl mx-auto`}>
          <AudioPlayerProvider audioSrc={audioUrl} onPlayEnd={handlePlayEnd} isAutoplay={!checkSubmitted} keepAwake={sessionSettings.keepAudioDeviceAwake}>
            <InteractiveCard
              currentItem={currentWord}
              isSubmitted={checkSubmitted}
              showFlippingAnimation={sessionSettings.showFlippingAnimation}
              isIgnored={false}
              isGraded={userChoice !== null}
              showTranslation={checkSubmitted && sessionSettings.showTranslation}
              answerResult={sendResult}
              onItemClick={handleWordClick}
              isHotkeysEnabled={!isAnyModalOpen}
              onIgnore={null}
              onSkip={handleSkipClick}
              onReport={handleReportClick}
              onSeeAlso={handleSeeAlsoClick}
              onExamples={handleExamplesClick}
              levelUpAlertState={{ show: false, level: '', type: '', prevLevel: undefined }}
              learnedWordIds={learnedWordIds}
              onVoiceChange={setSelectedVoice}
              currentVoice={selectedVoice}
              userInput={userInput}
              sessionSettings={sessionSettings}
              shrinkToContent={!sessionSettings.simpleAnswerMode && isPenMode}
            />
          </AudioPlayerProvider>
        </div>

        {/* Input section */}
        <div className="flex-shrink-0 w-full bg-background-secondary border-t border-border z-50">
          <div className="w-full max-w-4xl mx-auto p-4 session-input-area">
            {sessionSettings.simpleAnswerMode ? (
              <SimpleAnswerSection
                showResultCard={checkSubmitted}
                onReveal={() => setCheckSubmitted(true)}
                onChoice={handleSimpleModeChoice}
                userChoice={userChoice}
                isIgnored={false}
                onNext={handleNextWord}
              />
            ) : (
              <InputSection
                ref={inputRef}
                checkSubmitted={checkSubmitted}
                userInput={userInput}
                inputResults={inputResults}
                setUserInput={setUserInput}
                handleCheck={handleCheck}
                handleNext={handleNextWord}
                isCorrect={isCorrect}
                isIgnored={false}
                isAnyModalOpen={isAnyModalOpen}
                inputMode={sessionSettings?.inputMode || 'mixed'}
                autoConvertToKana={sessionSettings?.autoConvertToKana ?? true}
                reducedAnimations={sessionSettings?.reducedAnimations}
              />
            )}
          </div>
        </div>

        {/* Palm rest spacer: only in pen mode */}
        {!sessionSettings.simpleAnswerMode && isPenMode && <div className="flex-1 min-h-0 pointer-events-none" />}
      </div>
    </div>
  );
};

export default CramWordsSessionPage;